import React, { useState } from 'react';
import AppLayout from '../components/AppLayout';
import Button from '../components/ui/Button';
import UserProfile from '../components/settings/UserProfile';
import CompanyInfo from '../components/settings/CompanyInfo';
import InstallCapacity from '../components/settings/InstallCapacity';
import NotificationPreferences from '../components/settings/NotificationPreferences';
import DataManagement from '../components/settings/DataManagement';
import Appearance from '../components/settings/Appearance';
import { useJobs } from '../hooks/useJobs';
import { useDayNotes } from '../hooks/useDayNotes';
import { AppWindow, Mail } from 'lucide-react';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const OCHRE  = '#A6752A';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export default function SettingsPage() {
  const { jobs } = useJobs();
  const { dayNotes } = useDayNotes();
  const [sendingEmail, setSendingEmail] = useState(false);

  // Calculate stats for daily email
  const todayJobs = jobs.filter(job => {
    if (!job.scheduledDate) return false;
    const today = new Date();
    const scheduled = new Date(job.scheduledDate);
    return scheduled.toDateString() === today.toDateString();
  });

  const stats = {
    unscheduled: jobs.filter(j => !j.scheduledDate && j.status !== 'completed' && j.status !== 'cancelled').length,
    completed: todayJobs.filter(j => j.status === 'completed').length,
    repairs: todayJobs.filter(j => j.isRepair).length
  };

  async function handleSendDailyEmail() {
    setSendingEmail(true);

    // Simulate email sending (replace with actual Firebase function call)
    setTimeout(() => {
      alert('Daily email sent successfully!');
      setSendingEmail(false);
    }, 2000);
  }

  const breadcrumb = 'Dashboard › Settings';

  // Calculate furthest scheduled date
  const furthestScheduled = jobs
    .filter(j => j.scheduledDate)
    .map(j => new Date(j.scheduledDate))
    .sort((a, b) => b - a)[0];

  // Calculate estimated sold-out date (simplified)
  const unscheduledSold = jobs.filter(j => 
    !j.scheduledDate && 
    j.status === 'sold'
  );
  
  const daysToSchedule = Math.ceil(unscheduledSold.length / 1.6); // 1.6 jobs/day capacity
  const estimatedSoldOut = new Date();
  estimatedSoldOut.setDate(estimatedSoldOut.getDate() + daysToSchedule);

  const cardStyle = { background: PAPER, borderRadius: '16px', padding: '20px 24px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', marginBottom: '24px' };

  return (
    <AppLayout
      title="Settings"
      breadcrumb={breadcrumb}
    >
      <div style={{ fontFamily: FONT_SANS }}>
        {/* Header */}
        <div style={cardStyle}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{
                fontFamily: FONT_DISPLAY,
                fontSize: '24px',
                fontWeight: '600',
                color: INK,
                margin: '0 0 4px 0'
              }}>
                Settings
              </h2>
              <p style={{
                fontSize: '13px',
                color: STONE,
                margin: 0
              }}>
                The Window Place LLC
              </p>
            </div>

            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: WALNUT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}>
              <AppWindow size={26} strokeWidth={2} />
            </div>
          </div>
        </div>

        {/* User Profile */}
        <div style={cardStyle}>
          <UserProfile />
        </div>

        {/* Actions */}
        <div style={cardStyle}>
          <h3 style={{
            fontSize: '15px',
            fontWeight: '600',
            color: INK,
            marginBottom: '16px'
          }}>
            Actions
          </h3>

          <Button
            onClick={handleSendDailyEmail}
            disabled={sendingEmail}
            variant="secondary"
            style={{
              width: '100%',
              background: 'transparent',
              borderColor: OCHRE,
              color: OCHRE
            }}
          >
            <Mail size={15} strokeWidth={2} /> {sendingEmail ? 'Sending...' : 'Send Daily Email Now'}
          </Button>

          <div style={{
            marginTop: '12px',
            fontSize: '12px',
            color: STONE,
            padding: '12px',
            background: `${OCHRE}0d`,
            borderRadius: '8px'
          }}>
            Sends today's install summary to all email recipients
          </div>
        </div>

        {/* System Diagnostics */}
        <div style={cardStyle}>
          <h3 style={{
            fontSize: '15px',
            fontWeight: '600',
            color: INK,
            marginBottom: '16px'
          }}>
            System Diagnostics
          </h3>

          <div style={{
            display: 'grid',
            gap: '12px'
          }}>
            <DiagnosticRow label="Jobs Loaded" value={jobs.length} />
            <DiagnosticRow label="Jobs Today" value={todayJobs.length} />
            <DiagnosticRow label="Unscheduled Jobs" value={stats.unscheduled} />
            <DiagnosticRow
              label="Last Refresh"
              value={new Date().toLocaleTimeString()}
            />
          </div>
        </div>

        {/* Two Column Layout */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px',
          marginBottom: '24px'
        }}>
          <div style={{ ...cardStyle, marginBottom: 0 }}>
            <CompanyInfo />
          </div>

          <div style={{ ...cardStyle, marginBottom: 0 }}>
            <InstallCapacity />
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px',
          marginBottom: '24px'
        }}>
          <div style={{ ...cardStyle, marginBottom: 0 }}>
            <NotificationPreferences />
          </div>

          <div style={{ ...cardStyle, marginBottom: 0 }}>
            <DataManagement />
          </div>
        </div>

        <div style={cardStyle}>
          <Appearance />
        </div>

        {/* Footer */}
        <div style={cardStyle}>
          <div style={{
            textAlign: 'center',
            padding: '20px'
          }}>
            {furthestScheduled && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                fontSize: '13px',
                fontWeight: '600',
                color: WALNUT,
                marginBottom: '8px'
              }}>
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: WALNUT }} />
                Scheduled until: {furthestScheduled.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            )}

            {unscheduledSold.length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                fontSize: '13px',
                fontWeight: '600',
                color: OCHRE,
                marginBottom: '16px'
              }}>
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: OCHRE }} />
                Sold-out until: {estimatedSoldOut.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            )}

            <div style={{
              fontSize: '12px',
              color: STONE
            }}>
              © {new Date().getFullYear()} The Window Place LLC
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function DiagnosticRow({ label, value }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: '1px solid rgba(139,69,19,0.08)'
    }}>
      <span style={{
        fontSize: '13px',
        fontWeight: '600',
        color: STONE
      }}>
        {label}
      </span>
      <span style={{
        fontSize: '13px',
        color: INK,
        fontWeight: '600'
      }}>
        {value}
      </span>
    </div>
  );
}